"use client";

/**
 * ToolProgress - Progress indicator component
 *
 * Supports multiple variants: linear, circular, and dots
 */

import { Progress, Spinner } from "@/ui";
import { colors, spacing, textStyles } from "@/ui/tokens";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

export interface ToolProgressProps {
  value?: number; // 0-100, undefined for indeterminate
  variant?: "linear" | "circular" | "dots";
  label?: string;
  className?: string;
  size?: "small" | "medium" | "large";
}

/**
 * Animated dots progress indicator
 */
function DotsProgress({ size = "medium" }: { size?: "small" | "medium" | "large" }) {
  const dotSize = size === "small" ? 6 : size === "large" ? 10 : 8;
  const gap = size === "small" ? 4 : size === "large" ? 8 : 6;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: `${gap}px` }}>
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          style={{
            width: dotSize,
            height: dotSize,
            borderRadius: "50%",
            backgroundColor: "currentColor",
          }}
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.5, 1, 0.5],
          }}
          transition={{
            duration: 1,
            repeat: Infinity,
            delay: i * 0.2,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
}

export function ToolProgress({
  value,
  variant = "linear",
  label,
  className,
  size = "medium",
}: ToolProgressProps) {
  const isIndeterminate = value === undefined;

  if (variant === "dots") {
    return (
      <div
        className={cn(className)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: spacing[2],
          color: colors.action.product,
        }}
      >
        <DotsProgress size={size} />
        {label && (
          <span
            style={{
              ...textStyles.caption,
              color: colors.text.muted,
              fontWeight: 500,
            }}
          >
            {label}
          </span>
        )}
      </div>
    );
  }

  if (variant === "circular") {
    const spinnerSize = size === "small" ? "xs" : size === "large" ? "md" : "sm";

    return (
      <div
        className={cn(className)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: spacing[2],
        }}
      >
        <Spinner size={spinnerSize} />
        {label && (
          <span
            style={{
              ...textStyles.caption,
              color: colors.text.muted,
              fontWeight: 500,
            }}
          >
            {label}
          </span>
        )}
      </div>
    );
  }

  // Linear variant (default)
  const progressSize = size === "small" ? "sm" : size === "large" ? "lg" : "md";

  return (
    <div className={cn(className)} style={{ width: "100%" }}>
      <Progress
        value={isIndeterminate ? 0 : value ?? 0}
        label={label}
        showValue={!isIndeterminate && value !== undefined}
        size={progressSize}
        indeterminate={isIndeterminate}
      />
    </div>
  );
}

export default ToolProgress;
